import { IResource } from "@gettactic/api";
import { useBooleanState } from "@gettactic/helpers";
import { useMap } from "@webV2/map/MapProvider";
import React from "react";
import { MapStackSwitcherItem } from "./MapStackSwitcherItem";

export interface MapStackSwitcherProps {
  floorContainerRef: React.RefObject<HTMLDivElement>;
  onReady?: () => void;
  onFloorSelected?: (floor: IResource) => void;
}

export const MapStackSwitcher: React.FC<MapStackSwitcherProps> = ({
  floorContainerRef,
  onReady,
  onFloorSelected
}) => {
  const { floors, setFloor, fullscreenMapDisclosure } = useMap();

  const [hoveringIndex, setHoveringIndex] = React.useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const isFlattening = useBooleanState();

  const availableFloors =
    floors?.filter((x) => !!x.map_url || !!x.map_data) || [];

  const [mapContainerRef, setMapContainerRef] =
    React.useState<HTMLDivElement | null>(null);

  const itemsLoadedRef = React.useRef<(SVGSVGElement | undefined | null)[]>([]);
  const isAllItemsLoaded = useBooleanState();

  const handleItemMouseEnter = (hoveringIndex: number) => {
    if (selectedIndex !== null) return;
    setHoveringIndex(hoveringIndex);
  };

  const handleItemMouseLeave = (index: number) => {
    if (selectedIndex !== null) return;
    setHoveringIndex(null);
  };

  const handleItemClick = (index: number) => {
    if (selectedIndex !== null) return;
    const floor = availableFloors[index];
    if (floor) {
      setSelectedIndex(index);
      setTimeout(() => {
        isFlattening.true();
        setTimeout(() => {
          onFloorSelected?.(floor);
        }, 300);
      }, 1000);
    }
  };

  return (
    <>
      <div
        className={`absolute top-24 right-0 bottom-0 left-0 md:top-0 [.v2-embedded-map_&]:left-0 ${fullscreenMapDisclosure.isOpen ? "md:left-0" : "md:left-[425px]"}`}
        ref={setMapContainerRef}
      />
      {mapContainerRef && (
        <div
          className={`absolute top-24 right-0 bottom-0 left-0 md:top-0 [.v2-embedded-map_&]:left-0 ${fullscreenMapDisclosure.isOpen ? "md:left-0" : "md:left-[425px]"}`}
        >
          <div
            className={"h-full w-full overflow-hidden"}
            style={{
              perspective: "1200px",
              pointerEvents: "none"
            }}
          >
            <div
              className={
                "item-container transition-opacity transition-transform"
              }
              style={{
                position: "absolute",
                transitionDuration: "1s",
                inset: 0,
                transformStyle: "preserve-3d",
                pointerEvents: "none",
                opacity: isAllItemsLoaded.state ? 1 : 0,
                transform:
                  isAllItemsLoaded.state && !isFlattening.state
                    ? "scale(0.8) rotateX(60deg)"
                    : undefined
              }}
            >
              {availableFloors.map((floor, index) => (
                <MapStackSwitcherItem
                  key={floor.id}
                  floor={floor}
                  floorContainerRef={floorContainerRef}
                  mapContainerRef={{ current: mapContainerRef }}
                  index={index}
                  isAllItemsLoaded={isAllItemsLoaded.state}
                  onItemLoaded={(svg) => {
                    itemsLoadedRef.current.push(svg);
                    if (
                      itemsLoadedRef.current.length === availableFloors.length
                    ) {
                      isAllItemsLoaded.true();
                      onReady?.();
                    }
                  }}
                  floorItemsCount={floors?.length || 0}
                  onMouseEnter={handleItemMouseEnter}
                  onMouseLeave={handleItemMouseLeave}
                  onClick={handleItemClick}
                  hoveringIndex={hoveringIndex}
                  selectedIndex={selectedIndex}
                  isFlattening={isFlattening.state}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
